import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../components/Header.jsx";
import Footer from "../components/Footer.jsx";

// What the user can be reviewing — order matters for the dropdown.
// Each entry maps to one or more canonical tags applied at submit time.
const SOURCES = [
  {
    group: "Live events",
    items: [
      { value: "atai-2026", label: "All Things AI 2026 Conference", tags: ["atai-2026"] },
      { value: "all-things-ai-live", label: "All Things AI Live (meetup)", tags: ["all-things-ai-live"] },
      { value: "tri-women-in-stem", label: "Triangle Women in STEM", tags: ["tri-women-in-stem"] },
      { value: "ai-forward", label: "AI Forward (Charlotte)", tags: ["ai-forward"] },
      { value: "live-event-other", label: "Other live event / conference", tags: ["live-event"] },
    ],
  },
  {
    group: "Training",
    items: [
      { value: "aios-desktop", label: "AIOS Desktop course", tags: ["aios-desktop"] },
      { value: "ai-for-gtm", label: "AI for GTM workshop", tags: ["ai-for-gtm"] },
      { value: "training-other", label: "Other training / workshop", tags: ["training"] },
    ],
  },
  {
    group: "Newsletter",
    items: [
      { value: "newsletter", label: "The AI Enterprise newsletters", tags: ["newsletter"] },
      { value: "ai-tangle", label: "AI Tangle (Mon/Thu)", tags: ["newsletter", "ai-tangle"] },
      { value: "ai-advantage", label: "AI Advantage (Wed)", tags: ["newsletter", "ai-advantage"] },
      { value: "ai-deep-dive", label: "AI Deep Dive (Fri)", tags: ["newsletter", "ai-deep-dive"] },
      { value: "ai-lessons", label: "AI Lessons (Tue)", tags: ["newsletter", "ai-lessons"] },
    ],
  },
  {
    group: "Other",
    items: [
      { value: "general", label: "General / not listed above", tags: [] },
    ],
  },
];

const ALL_ITEMS = SOURCES.flatMap((g) => g.items);
const lookupSource = (v) => ALL_ITEMS.find((x) => x.value === v);

export default function SubmitPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialEvent = params.get("event") || "";

  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_company: "",
    customer_tagline: "",
    rating: 5,
    title: "",
    text: "",
    source: lookupSource(initialEvent)?.value || "",
    visibility: "public", // 'public' | 'private'
    consent: false,
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);

  // If the URL param matches a known source, lock the banner to it
  const lockedSource = lookupSource(initialEvent);

  const onChange = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    // Consent only required for public submissions
    if (form.visibility === "public" && !form.consent) {
      setError("Please confirm you're okay with us publishing this — or switch to private.");
      return;
    }
    if (!form.source) {
      setError("Please tell us what you're reviewing.");
      return;
    }
    setStatus("submitting");
    setError(null);
    try {
      const sourceMeta = lookupSource(form.source);
      const tags = sourceMeta ? [...sourceMeta.tags] : [];
      if (form.visibility === "private") tags.push("private");
      const res = await fetch("/api/submit-review", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          tags,
          rating: Number(form.rating),
          source_event: form.source,
          visibility: form.visibility,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Submission failed (${res.status})`);
      }
      const qs = form.source ? `?event=${encodeURIComponent(form.source)}` : "";
      nav(`/thanks${qs}`);
    } catch (err) {
      setStatus("error");
      setError(err.message);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-16">
        {lockedSource ? (
          <div className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 rounded-full bg-flame/10 border border-flame/30 text-flame text-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-flame" />
            {lockedSource.label}
          </div>
        ) : null}
        <h1 className="text-3xl md:text-5xl font-bold tracking-tight">
          Leave a review
        </h1>
        <p className="mt-4 text-white/70">
          {lockedSource
            ? `Tell us what you thought about ${lockedSource.label}. As thanks, you'll get the slides and follow-up notes.`
            : "Tell us what The AIE Network is getting right. In return, you'll get a free download."}
        </p>

        {/* A note from Mark */}
        <aside className="mt-8 card p-5 flex gap-4 items-start">
          <img
            src="https://d2xsxph8kpxj0f.cloudfront.net/310419663031482000/JUZFF9A2F5U2mhUoJRB8yh/mark-hinkle-headshot-cropped_2a3cbcfa.webp"
            alt="Mark Hinkle"
            className="w-14 h-14 rounded-full flex-none border-2 border-flame/40 object-cover"
          />
          <div className="text-sm">
            <p className="text-white/85 leading-relaxed">
              Reviews help me improve our products and grow our business.
              Your feedback is critical — even the rough stuff. I read every word.
            </p>
            <p className="text-white/50 text-xs mt-2">
              — Mark Hinkle, founder of The AIE Network
            </p>
          </div>
        </aside>

        <form onSubmit={submit} className="mt-10 space-y-5">
          <Field label="What's this review about?" required hint="So we can route the right thank-you assets back to you.">
            <select
              required
              value={form.source}
              onChange={onChange("source")}
              className={input}
              disabled={Boolean(lockedSource)}
            >
              <option value="" disabled>Pick one…</option>
              {SOURCES.map((g) => (
                <optgroup key={g.group} label={g.group}>
                  {g.items.map((it) => (
                    <option key={it.value} value={it.value}>{it.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </Field>

          <Field label="Your name" required>
            <input required value={form.customer_name} onChange={onChange("customer_name")} className={input} />
          </Field>
          <Field label="Email" required hint="Not shown publicly. We use it only to verify you and find your headshot.">
            <input required type="email" value={form.customer_email} onChange={onChange("customer_email")} className={input} />
          </Field>
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Company">
              <input value={form.customer_company} onChange={onChange("customer_company")} className={input} />
            </Field>
            <Field label="Title">
              <input value={form.customer_tagline} onChange={onChange("customer_tagline")} className={input} />
            </Field>
          </div>
          <Field label="Rating">
            <select value={form.rating} onChange={onChange("rating")} className={input}>
              {[5,4,3,2,1].map(n => <option key={n} value={n}>{n} stars</option>)}
            </select>
          </Field>
          <Field label="Headline (optional)">
            <input value={form.title} onChange={onChange("title")} className={input} placeholder="One-liner summary" />
          </Field>
          <Field label="Your review" required>
            <textarea required value={form.text} onChange={onChange("text")} className={input + " h-32"} />
          </Field>

          <fieldset className="space-y-2">
            <span className="text-sm font-medium">Visibility</span>
            <div className="grid sm:grid-cols-2 gap-2">
              <label className={`card p-3 cursor-pointer flex gap-3 items-start ${form.visibility === "public" ? "ring-2 ring-flame" : ""}`}>
                <input
                  type="radio"
                  name="visibility"
                  value="public"
                  checked={form.visibility === "public"}
                  onChange={onChange("visibility")}
                  className="mt-1"
                />
                <div className="text-sm">
                  <div className="font-semibold">Public 🌎</div>
                  <div className="text-white/60 text-xs">Mark may publish your review on reviews.theaie.net and in marketing.</div>
                </div>
              </label>
              <label className={`card p-3 cursor-pointer flex gap-3 items-start ${form.visibility === "private" ? "ring-2 ring-flame" : ""}`}>
                <input
                  type="radio"
                  name="visibility"
                  value="private"
                  checked={form.visibility === "private"}
                  onChange={onChange("visibility")}
                  className="mt-1"
                />
                <div className="text-sm">
                  <div className="font-semibold">Private 🔒</div>
                  <div className="text-white/60 text-xs">Just for Mark — won't be published anywhere.</div>
                </div>
              </label>
            </div>
          </fieldset>

          {form.visibility === "public" ? (
            <label className="flex items-start gap-3 text-sm text-white/70">
              <input type="checkbox" checked={form.consent} onChange={onChange("consent")} className="mt-1" />
              I'm happy for Mark and The AIE Network to publish this review on their sites and in marketing.
            </label>
          ) : null}

          {error ? <p className="text-red-400 text-sm">{error}</p> : null}

          <button type="submit" disabled={status === "submitting"} className="btn-flame disabled:opacity-60">
            {status === "submitting" ? "Sending…" : "Submit review & claim download"}
          </button>
        </form>
      </main>
      <Footer />
    </div>
  );
}

const input = "w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 focus:outline-none focus:border-flame disabled:opacity-60";

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <span className="text-sm font-medium">
        {label} {required ? <span className="text-flame">*</span> : null}
      </span>
      {hint ? <span className="block text-xs text-white/50 mt-0.5">{hint}</span> : null}
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
